import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EntitiesList from "components/Entities/EntitiesList";
import EntitiesSort from "components/Entities/EntitiesSort";
import ScrollHideContainer from "ui/ScrollHideContainer";
import LoadingSkeleton from "ui/LoadingSkeleton";
import EmptyState from "ui/EmptyState";
import PostCreateButton from "ui/PostCreateButton";
import { EntityType, Issue, Sort } from "util/constants";

interface IssueFeedProps {
  issue: Issue;
  hideSort?: boolean;
}

/**
 * IssueFeed renders a mixed feed of entities tied to an issue.
 * It uses the shared entities list and filters by issue ID.
 * @param issue - The issue being viewed.
 * @returns The issue feed component.
 */
const IssueFeed: React.FC<IssueFeedProps> = ({ issue, hideSort = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const sort = (searchParams.get("sort") as Sort) || "new";
  const [isLoading, setIsLoading] = useState(false);

  // Keep entity types stable to avoid unnecessary re-fetches.
  const entityTypes = useMemo<EntityType[]>(
    () => ["impact", "teabate", "solution", "comment", "crosspost", "action"],
    []
  );

  /**
   * Update the URL with a new sort option for the feed.
   * @param newSort - The selected sort option.
   */
  const handleSortChange = (newSort: Sort) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("sort", newSort);
    navigate(`${location.pathname}?${newSearchParams.toString()}`);
  };

  /**
   * Track loading state from the entities list.
   * @param loading - Whether the list is currently loading.
   */
  const handleLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  return (
    <div className="issue-feed">
      {!hideSort && (
        <ScrollHideContainer
          className="z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
          hideOffset={57}
          threshold={56}
        >
          <div className="issue-feed-toolbar flex gap-1 items-center py-2 px-2">
            <EntitiesSort
              entityType="issue"
              sort={sort}
              onSortChange={handleSortChange}
            />
          </div>
        </ScrollHideContainer>
      )}
      <div className="issue-feed-create px-2 py-2">
        <PostCreateButton
          className="w-full"
          buttonText="Spill"
          tooltip="Create a post"
          initialIssue={issue}
        />
      </div>
      <EntitiesList
        entityTypes={entityTypes}
        sort={sort}
        searchParams={searchParams}
        issueId={issue.id}
        isLoading={handleLoading}
        emptyState={
          <EmptyState
            title="Nothing added for this issue yet"
            subtitle="Be the first to spill the tea on this issue!"
            className="py-8"
          />
        }
      />
      {isLoading && <LoadingSkeleton variant="entity-card" count={5} />}
    </div>
  );
};

export default IssueFeed;
